import axios from 'axios';
import { httpApi } from './http';
import type { CardLinkDoc, CardLinkEntryType, CardLinkResponse } from '../types/cardsLink';

export class CardsLinkService {
  static async getByCardId(cardId: string): Promise<CardLinkResponse<CardLinkDoc>> {
    try {
      const response = await httpApi.get<CardLinkResponse<CardLinkDoc>>(`/cardslink/card/${encodeURIComponent(cardId)}`);
      if (!response.data) throw new Error('No data received from server');
      if (!response.data.success) throw new Error(response.data.error || 'API request failed');
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const status = error.response?.status;
        const message = (error.response?.data as any)?.message || (error.response?.data as any)?.error;
        throw Object.assign(new Error(message || 'Failed to load card links'), { status });
      }
      throw new Error('An unexpected error occurred');
    }
  }

  static async getById(cardLinkId: string): Promise<CardLinkResponse<CardLinkDoc>> {
    try {
      const response = await httpApi.get<CardLinkResponse<CardLinkDoc>>(`/cardslink/${encodeURIComponent(cardLinkId)}`);
      if (!response.data) throw new Error('No data received from server');
      if (!response.data.success) throw new Error(response.data.error || 'API request failed');
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const message = (error.response?.data as any)?.message || (error.response?.data as any)?.error;
        throw new Error(message || 'Failed to load card links');
      }
      throw new Error('An unexpected error occurred');
    }
  }

  static async create(payload: {
    cardId: string;
    bank_name?: string;
    credit_name?: string;
    entries?: Array<{ type: CardLinkEntryType; url?: string; content?: string }>;
  }): Promise<CardLinkResponse<CardLinkDoc>> {
    try {
      const response = await httpApi.post<CardLinkResponse<CardLinkDoc>>('/cardslink', payload);
      if (!response.data) throw new Error('No data received from server');
      if (!response.data.success) throw new Error(response.data.error || 'API request failed');
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const message = (error.response?.data as any)?.message || (error.response?.data as any)?.error;
        throw new Error(message || 'Failed to create card links');
      }
      throw new Error('An unexpected error occurred');
    }
  }

  static async update(cardLinkId: string, payload: { bank_name?: string; credit_name?: string }): Promise<CardLinkResponse<CardLinkDoc>> {
    try {
      const response = await httpApi.put<CardLinkResponse<CardLinkDoc>>(`/cardslink/${encodeURIComponent(cardLinkId)}`, payload);
      if (!response.data) throw new Error('No data received from server');
      if (!response.data.success) throw new Error(response.data.error || 'API request failed');
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const message = (error.response?.data as any)?.message || (error.response?.data as any)?.error;
        throw new Error(message || 'Failed to update card links');
      }
      throw new Error('An unexpected error occurred');
    }
  }

  static async delete(cardLinkId: string): Promise<CardLinkResponse<CardLinkDoc>> {
    try {
      const response = await httpApi.delete<CardLinkResponse<CardLinkDoc>>(`/cardslink/${encodeURIComponent(cardLinkId)}`);
      if (!response.data) throw new Error('No data received from server');
      if (!response.data.success) throw new Error(response.data.error || 'API request failed');
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const message = (error.response?.data as any)?.message || (error.response?.data as any)?.error;
        throw new Error(message || 'Failed to delete card links');
      }
      throw new Error('An unexpected error occurred');
    }
  }

  static async addEntry(cardLinkId: string, payload: { type: CardLinkEntryType; url?: string; content?: string }): Promise<CardLinkResponse<CardLinkDoc>> {
    try {
      const response = await httpApi.post<CardLinkResponse<CardLinkDoc>>(
        `/cardslink/${encodeURIComponent(cardLinkId)}/entries`,
        payload
      );
      if (!response.data) throw new Error('No data received from server');
      if (!response.data.success) throw new Error(response.data.error || 'API request failed');
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const message = (error.response?.data as any)?.message || (error.response?.data as any)?.error;
        throw new Error(message || 'Failed to add card link entry');
      }
      throw new Error('An unexpected error occurred');
    }
  }

  static async updateEntry(
    cardLinkId: string,
    entryId: string,
    payload: { type: CardLinkEntryType; url?: string; content?: string }
  ): Promise<CardLinkResponse<CardLinkDoc>> {
    try {
      const response = await httpApi.put<CardLinkResponse<CardLinkDoc>>(
        `/cardslink/${encodeURIComponent(cardLinkId)}/entries/${encodeURIComponent(entryId)}`,
        payload
      );
      if (!response.data) throw new Error('No data received from server');
      if (!response.data.success) throw new Error(response.data.error || 'API request failed');
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const message = (error.response?.data as any)?.message || (error.response?.data as any)?.error;
        throw new Error(message || 'Failed to update card link entry');
      }
      throw new Error('An unexpected error occurred');
    }
  }

  static async deleteEntry(cardLinkId: string, entryId: string): Promise<CardLinkResponse<CardLinkDoc>> {
    try {
      const response = await httpApi.delete<CardLinkResponse<CardLinkDoc>>(
        `/cardslink/${encodeURIComponent(cardLinkId)}/entries/${encodeURIComponent(entryId)}`
      );
      if (!response.data) throw new Error('No data received from server');
      if (!response.data.success) throw new Error(response.data.error || 'API request failed');
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const message = (error.response?.data as any)?.message || (error.response?.data as any)?.error;
        throw new Error(message || 'Failed to delete card link entry');
      }
      throw new Error('An unexpected error occurred');
    }
  }
}
